import React from 'react';
import ReactECharts from 'echarts-for-react';
import { useTranslation } from 'react-i18next';
import { BatteryStatus } from '../../types/energyData';
import { EnergyDataService } from '../../services/energyDataService';

interface BatteryDetailViewProps {
  data: BatteryStatus;
  energyDataService: EnergyDataService;
}

export const BatteryDetailView: React.FC<BatteryDetailViewProps> = ({ data, energyDataService }) => {
  const { t } = useTranslation();
  
  // Get REAL time series data from JSON files
  const timeSeriesData = energyDataService.getBatteryTimeSeriesData();
  
  // Use real data if available, otherwise show current values
  const hasTimeSeriesData = timeSeriesData.socData.length > 0;
  
  const timeData = hasTimeSeriesData ? timeSeriesData.timeLabels : ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00', '24:00'];
  const socData = hasTimeSeriesData ? timeSeriesData.socData : [data.soc, data.soc, data.soc, data.soc, data.soc, data.soc, data.soc];
  const powerData = hasTimeSeriesData ? timeSeriesData.powerData : [data.chargePower, data.chargePower, data.chargePower, data.chargePower, data.chargePower, data.chargePower, data.chargePower];
  
  // Energy data should correlate with SOC
  const energyData = socData.map(soc => (soc / 100) * data.storedEnergy);

  const socChartOption = {
    title: {
      text: t('battery.batteryLevelOverTime'),
      left: 'center',
      textStyle: { fontSize: 16 }
    },
    tooltip: {
      trigger: 'axis',
      formatter: '{b}: {c}%'
    },
    xAxis: {
      type: 'category',
      data: timeData
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: 100,
      axisLabel: {
        formatter: '{value}%'
      }
    },
    series: [{
      data: socData,
      type: 'line',
      smooth: true,
      lineStyle: { color: '#10B981' },
      areaStyle: { color: 'rgba(16, 185, 129, 0.2)' }
    }]
  };

  const powerChartOption = {
    title: {
      text: t('battery.powerFlowOverTime'),
      left: 'center',
      textStyle: { fontSize: 16 }
    },
    tooltip: {
      trigger: 'axis',
      formatter: function(params: any) {
        const value = params[0].value;
        const type = value > 0 ? 'Charging' : value < 0 ? 'Discharging' : 'Idle';
        return `${params[0].axisValue}: ${Math.abs(value).toFixed(1)} kW (${type})`;
      }
    },
    xAxis: {
      type: 'category',
      data: timeData
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: '{value} kW'
      }
    },
    series: [{
      data: powerData,
      type: 'bar',
      itemStyle: {
        color: function(params: any) {
          return params.value > 0 ? '#10B981' : params.value < 0 ? '#EF4444' : '#6B7280';
        }
      }
    }]
  };

  const energyChartOption = {
    title: {
      text: t('battery.energyStoredOverTime'),
      left: 'center',
      textStyle: { fontSize: 16 }
    },
    tooltip: {
      trigger: 'axis',
      formatter: '{b}: {c} kWh'
    },
    xAxis: {
      type: 'category',
      data: timeData
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: '{value} kWh'
      }
    },
    series: [{
      data: energyData,
      type: 'line',
      smooth: true,
      lineStyle: { color: '#3B82F6' },
      areaStyle: { color: 'rgba(59, 130, 246, 0.2)' }
    }]
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">🔋 {t('battery.batteryManagementSystem')}</h2>
            <p className="text-gray-600">{t('battery.realTimeBatteryMonitoring')}</p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-green-600">{data.soc}%</div>
            <div className="text-sm text-gray-500">{t('battery.stateOfCharge')}</div>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <ReactECharts option={socChartOption} style={{ height: '300px' }} />
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <ReactECharts option={powerChartOption} style={{ height: '300px' }} />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <ReactECharts option={energyChartOption} style={{ height: '300px' }} />
      </div>

      {/* Key Metrics and Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Key Metrics */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">📊 {t('battery.keyMetrics')}</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('battery.currentSOC')}</span>
              <span className="font-semibold">{data.soc}%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('battery.chargeRate')}</span>
              <span className="font-semibold">{data.chargePower.toFixed(1)} kW</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('battery.dischargeRate')}</span>
              <span className="font-semibold">{data.dischargePower.toFixed(1)} kW</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('battery.energyStored')}</span>
              <span className="font-semibold">{data.storedEnergy.toFixed(1)} kWh</span>
            </div>
          </div>
        </div>

        {/* System Status */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">🔧 {t('battery.systemStatus')}</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('battery.operatingMode')}</span>
              <span className="font-semibold">{data.operatingStatus}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('battery.controlMode')}</span>
              <span className="font-semibold">{data.controlMode}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('battery.maxChargeRate')}</span>
              <span className="font-semibold">{data.maxChargeRate.toFixed(1)} kW</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">{t('battery.maxDischargeRate')}</span>
              <span className="font-semibold">{data.maxDischargeRate.toFixed(1)} kW</span>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};
